import React, { FunctionComponent } from "react";

import { TimeSeriesUserProfilesJSON } from "coral-common/common/lib/types/dashboard";

import { PieChart, Pie, Legend, ResponsiveContainer } from "recharts";
import { useMemo } from "react";
import colors from "coral-ui/theme/colors";
import { ReactText } from "react";

type WithDateTimestamp<T> = Omit<T, 'timestamp'> & { timestamp: Date };
type LegentFormatterEntry = { id?: string; type?: string; value: string | number; color?: string; payload?: { strokeDasharray: ReactText; payload?: { absoluteValue?: number, value: number } } };
interface Props {
  seriesUserProfiles: WithDateTimestamp<TimeSeriesUserProfilesJSON["seriesUserProfiles"]>;
  seriesActiveUsers: WithDateTimestamp<TimeSeriesUserProfilesJSON["seriesActiveUsers"]>;
  userProfilesNames?: Record<string, string>;
  userProfilesColors?: Record<string, string>;
}

export const OIDCActivityPie: FunctionComponent<Props> = ({seriesUserProfiles, seriesActiveUsers, userProfilesNames, userProfilesColors}) => {

  const latestUserProfilesReport = useMemo(() => {
    if (!seriesUserProfiles || seriesUserProfiles.length === 0) {
      return undefined;
    }
    
    return seriesUserProfiles[seriesUserProfiles.length - 1];

  }, [seriesUserProfiles]);

  const pieSectorsActiveUsers = useMemo(() => {
    if (!latestUserProfilesReport) {
      return [];
    }

    const {timestamp,  ...activityValues} = latestUserProfilesReport;
    const totalCount = Object.values(activityValues).reduce(
      (sum, value) => sum + value,
      0
    );

    const latest = seriesActiveUsers[seriesActiveUsers.length - 1];
    const count = latest?.count || 0;

    const percentMap = [{
        name: userProfilesNames?.['activeUsers'] || 'Active Users',
        value: (count / totalCount) * 100,
        fill: colors.yellow900,
        absoluteValue: count,
      },
      {
        name: userProfilesNames?.['inactiveUsers'] || 'Inactive Users',
        value: ((totalCount - count) / totalCount) * 100,
        fill: colors.grey600,
        absoluteValue: totalCount - count,
      }
    ];
 
    return percentMap;
  }, [ seriesActiveUsers, latestUserProfilesReport, userProfilesNames, userProfilesColors]);

  const pieSectorsUserProfiles = useMemo(() => {
    if (!latestUserProfilesReport) {
      return [];
    }

    const percentMap: {
      name: string;
      value: number;
      fill?: string; 
      absoluteValue?: number;
    }[] = [];

    const {timestamp,  ...activityValues} = latestUserProfilesReport;

    const keys = Object.keys(activityValues);
    const totalCount = Object.values(activityValues).reduce(
      (sum, value) => sum + value,
      0
    );

    for (const key of keys) {
      const value = activityValues[key];
      if (value) {
        const color = userProfilesColors?.[key] || colors.red400;
        percentMap.push({
          name: key,
          value: (value / totalCount) * 100,
          fill: color,
          absoluteValue: value,
        });
      }
    }

    return percentMap;
  }, [latestUserProfilesReport, userProfilesColors, userProfilesNames]);

  return (
      <div style={{ width: "100%", height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
      <PieChart>
          <Pie
            key="userProfilesPie"
            isAnimationActive={true}
            data={pieSectorsUserProfiles}
            cx="50%"
            cy="50%"
            dataKey="value"
            nameKey="name"
            innerRadius="50%"
            outerRadius="80%"
            label={(entry) => `${entry.value.toFixed(2)}%`}
          />

          <Pie
            key="activeUsersPie"
            isAnimationActive={true}
            data={pieSectorsActiveUsers}
            dataKey="value"
            nameKey="name"
            outerRadius="35%"
            cx="50%"
            cy="50%"
          />

          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            style={{ fontSize: '0.8rem' }}
            formatter={(value: string, pieEntry: LegentFormatterEntry) => {
              const entry = pieEntry.value;
              const payload = pieEntry?.payload?.payload;
              const absoluteEntry = payload?.absoluteValue || 0;

              if (entry) {
                return (
                  <span style={{ color: pieEntry.color }}>
                    { userProfilesNames?.[value] || value } #{absoluteEntry} ({payload?.value?.toFixed(2)}%)
                  </span>
                );
              }

              return value;
            }}
          />
      </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default OIDCActivityPie ;
